import type { CellData } from '../../../../common/table/TableTypes';
import { TiptapNodeType } from '../../../../common/tiptap/tiptapTypes';
import type { TiptapContent, TiptapContentMark, TiptapContentNode } from '../../../../common/tiptap/tiptapTypes';
import convertSimpleStringToTiptapContent from '../../../../common/tiptap/utils/createJsonContentUtils/convertSimpleStringToTiptapContent';
import { convertInlineStyleToTiptapMark } from '../../../../common/tiptap/conversion/editorConversion/toTiptap/convertDraftInlineStyleToTiptapMark';
import { getLocalisedContent } from '../utils/dataUtils/cell/tableCellDisplayUtils';

const applyMarksToTextNodes = (node: TiptapContentNode, marks: TiptapContentMark[]): TiptapContentNode => {
    if (node.type === TiptapNodeType.text && node.text) return { ...node, marks: [...(node.marks || []), ...marks] };
    if (!node.content) return node;

    return {
        ...node,
        content: node.content.map((child) => applyMarksToTextNodes(child, marks)),
    };
};

/**
 * Applies whole-cell text styles to plain value content when no richer Tiptap content exists yet.
 */
const applyCellTextStylesToTiptapContent = (content: TiptapContent, textStyles?: string[]): TiptapContent => {
    // Cell inline styles are represented in draftjs format, so we need to convert them
    //  to Tiptap marks before we can apply them to the content.
    const marks = textStyles?.map(convertInlineStyleToTiptapMark).filter(Boolean) as TiptapContentMark[] | undefined;
    if (!marks?.length) return content;

    return {
        ...content,
        content: content.content.map((node) => applyMarksToTextNodes(node, marks)),
    };
};

/**
 * Converts plain cell text into Tiptap content with optional whole-cell text styles.
 */
export const getTiptapContentFromPlainText = (plainText: string, textStyles?: string[]): TiptapContent =>
    applyCellTextStylesToTiptapContent(convertSimpleStringToTiptapContent(plainText), textStyles);

/**
 * Builds the Tiptap document from the cell's persisted content.
 */
export const getTiptapCellContent = ({
    cellData,
    locale,
}: {
    cellData: CellData | undefined;
    locale: string;
}): TiptapContent | null => {
    if (!cellData) return null;

    if (cellData.tiptapContent) return cellData.tiptapContent;

    const plainText = String(getLocalisedContent(cellData, locale) ?? '');
    return getTiptapContentFromPlainText(plainText, cellData.textStyle);
};

/**
 * Builds the Tiptap document that replaces persisted content when a cell edit starts with typed text.
 */
export const getTiptapCellOverwriteContent = ({
    cellData,
    overwriteContent,
}: {
    cellData: CellData | undefined;
    overwriteContent: string | undefined;
}): TiptapContent | null => {
    if (!cellData || !overwriteContent) return null;

    return getTiptapContentFromPlainText(overwriteContent, cellData.textStyle);
};
